import React, { useEffect, useLayoutEffect, useState } from 'react';

// Redux connect
import { shallowEqual } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// components
import Divider from 'components/Divider';
import Button from 'components/UiKit/Button';
import TextField from 'components/UiKit/FieldText';
import ErrorBoundary from 'components/ErrorBoundary';
import FieldPhone from 'components/UiKit/FieldPhone';
import FieldSelect from 'components/UiKit/FieldSelect';
import FieldCheckbox from 'components/UiKit/FieldCheckbox';

// Link
import { useNavigate } from 'react-router-dom';

// Lib
import * as yup from 'yup';
import { sanitize } from 'dompurify';
import { useWindowSize } from 'lib/hooks';
import { patternRxpCheckEng } from 'lib/helpers';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// Actions
import * as actionsCreator from '../reducer';

// Constants
import { codesFlag } from 'constans/code-flag';
import { selectStyle } from 'constans/react-select-style';

// Schema
const schema = yup.object({
	lastName: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').required(),
	position: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').required(),
	firstName: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').required(),
	companyName: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').required(),
	email: yup.string().email().required(),
	advertConsent: yup.boolean().nullable(),
	password: yup
		.string()
		.min(8, 'Password must be at least 8 characters')
		.matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
		.matches(/[a-z]/, 'Password must contain at least one lowercase letter')
		.matches(/[0-9]/, 'Password must contain at least one number')
		.matches(
			/[!()\-\\.?[\]_`~;:!@#$%^&*+=<>,]/,
			'Password must contain at least one special character'
		)
		.required(),
	confirmPassword: yup
		.string()
		.oneOf([yup.ref('password')], 'Passwords must match')
		.required(),
	title: yup.string().required('Title is required!'),
	confirmEmail: yup.string().email().oneOf([yup.ref('email')], 'Email must match').required(),
	phone: yup.string().min(5, 'Please type numbers only').matches(/^[^X]*$/, 'Please type numbers only').required('Number is not valid')
}).required();
type FormData = yup.InferType<typeof schema>;

export const SignUp = () => {
	const { width: screenWidth } = useWindowSize();

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const {
		status,
		loading,
		message,
		optionsTitle
	} = useAppSelector((store) => {
		return {
			status: store.auth.status,
			message: store.auth.message,
			optionsTitle: store.auth.optionsTitle,

			loading: store.auth.loadingSingUp
		};
	}, shallowEqual);

	const [isValidPhone, setIsValidPhone] = useState<boolean>(false);

	useEffect(() => () => {
		dispatch(actionsCreator.clearMessage());
	}, []);

	// Scroll to top after navigating
	useLayoutEffect(() => {
		window.scrollTo(0, 0);
	}, [navigate]);

	const { control, register, handleSubmit, formState: { errors } } = useForm<FormData>({
		resolver: yupResolver(schema),
		defaultValues: {
			advertConsent: true
		}
	});

	const onSubmit = (data: FormData) => {
		if (isValidPhone) {
			dispatch(actionsCreator.singUp({ ...data }));
		}
	};

	return (
		<ErrorBoundary>
			<div className="screen-signup">
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="screen-signup__form">

						<p className="screen-signup__text">
							This space allows you to manage your personal information, orders in the Enamine compounds store, news
							updates, and newsletter subscriptions.
						</p>

						<Divider size={screenWidth < 1024 ? 20 : 40} />

						<div className="flex flex-ai-center flex-direction-column-desktop">
							<TextField
								required
								name="firstName"
								label="First name *"
								placeholder="Your answer"
								isError={!!errors?.firstName}
								className="screen-signup__col mr30"
								register={register('firstName')}
								msgError={errors?.firstName ? errors?.firstName.message : ''}
							/>

							<TextField
								required
								name="lastName"
								label="Last name *"
								placeholder="Your answer"
								isError={!!errors?.lastName}
								className="screen-signup__col"
								msgError={errors?.lastName ? errors?.lastName.message : ''}
								register={register('lastName')}
							/>
						</div>

						<Divider size={screenWidth < 1024 ? 20 : 40} />

						<div className="flex flex-ai-center flex-direction-column-desktop">
							<Controller
								name="title"
								control={control}
								render={({ field: { onChange, value } }) => (
									<FieldSelect
										required
										name="title"
										label="Title *"
										placeholder="Select"
										styles={selectStyle}
										isError={!!errors?.title}
										className="screen-signup__col mr30"
										onChange={(val: any) => onChange(val.value)}
										msgError={errors?.title ? errors?.title?.message : ''}
										register={register('title', { required: true })}
										options={optionsTitle.map(x => ({ label: x.label, value: x.value }))}
									/>
								)}
							/>

							<TextField
								required
								name="position"
								label="Position *"
								placeholder="Your answer"
								isError={!!errors?.position}
								className="screen-signup__col"
								register={register('position')}
								msgError={errors?.position ? errors?.position.message : ''}
							/>
						</div>

						<Divider size={screenWidth < 1024 ? 20 : 40} />

						<div className="flex flex-ai-center flex-direction-column-desktop">
							<Controller
								name="phone"
								control={control}
								rules={{ required: true }}
								render={({ field: { onChange, value } }) => (
									<FieldPhone
										big
										required
										name="phone"
										maskChar="X"
										value={value}
										countryCode="UA"
										options={codesFlag}
										placeholderChar="X"
										label="Phone number *"
										style={{ width: '100%' }}
										setError={setIsValidPhone}
										error={!!errors?.phone?.message}
										className="screen-signup__col mr30"
										msgError={errors?.phone ? errors?.phone.message : ''}
										onChange={(name: string, value: string) => onChange(value)}
										register={register('phone', { required: true, pattern: /^\S+@\S+$/i })}
									/>
								)}
							/>

							<TextField
								required
								name="companyName"
								label="Company name *"
								placeholder="Your answer"
								className="screen-signup__col"
								isError={!!errors?.companyName}
								register={register('companyName')}
								msgError={errors?.companyName ? errors?.companyName.message : ''}
							/>
						</div>

						<Divider size={screenWidth < 1024 ? 20 : 40} />

						<div className="flex flex-ai-center flex-direction-column-desktop">
							<TextField
								required
								name="email"
								label="Email address*"
								isError={!!errors?.email}
								className="screen-signup__col mr30"
								placeholder="yourcompany@email.com"
								msgError={errors.email ? errors.email.message : ''}
								register={register('email', { required: 'Email is required', pattern: /^\S+@\S+$/i })}
							/>

							<TextField
								required
								name="confirmEmail"
								label="Confirm Email address *"
								isError={!!errors?.confirmEmail}
								className="screen-signup__col"
								placeholder="yourcompany@email.com"
								msgError={errors?.confirmEmail ? errors?.confirmEmail.message : ''}
								register={register('confirmEmail', { required: 'Confirm email is required', pattern: /^\S+@\S+$/i })}
							/>
						</div>

						<Divider size={screenWidth < 1024 ? 20 : 40} />

						<div className="flex flex-ai-center flex-direction-column-desktop">
							<TextField
								required
								isIconInfo
								name="password"
								isShowPassword
								autoComplete="off"
								label="Create Password*"
								placeholder="Your password"
								isError={!!errors?.password}
								title="Password Requirements
                    At least 8 characters.
                    Our recommendation password sould be a combination of uppercase letters, lowercase letters, numbers, and symbols.
                    Not a word that can be found in a dictionary or the name of a person, character, product, or organization."
								className="screen-signup__col mr30"
								msgError={errors?.password ? errors?.password.message : ''}
								register={register('password', { required: 'Password is required' })}
							/>

							<TextField
								required
								isShowPassword
								autoComplete="off"
								name="confirmPassword"
								label="Confirm Password *"
								placeholder="Your password"
								className="screen-signup__col"
								isError={!!errors?.confirmPassword}
								msgError={errors?.confirmPassword ? errors?.confirmPassword.message : ''}
								register={register('confirmPassword', { required: 'Position is required' })}
							/>
						</div>

						<Divider size={screenWidth < 1024 ? 20 : 40} />

						<Controller
							name="advertConsent"
							control={control}
							rules={{ required: true }}
							render={({ field: { onChange, value } }) => {
								return (
									<FieldCheckbox
										value={!!value}
										name="checkboxRegistration"
										onChange={(val: any) => onChange(val)}
										isError={!!errors?.advertConsent}
										msgError={errors?.advertConsent ? errors?.advertConsent.message : ''}
										register={register('advertConsent', { required: 'Advert consent is required' })}
										label={`I would like to receive marketing information about products or services of Enamine. This information can be sent via email, sms messages or post. Your information also can be used to deliver personalized messages or advertising on social media or other digital platforms. You can request an unsubscription at any time.`}
									/>
								);
							}}
						/>

						<Divider size={screenWidth < 1024 ? 20 : 40} />

						<p
							className="screen-signup__info"
							dangerouslySetInnerHTML={{ __html: sanitize(`By creating an account, you agree to Enamine's <a href="/terms-of-service" target="_blank">Terms of Service</a> and <a href="/privacy-policy" target="_blank">Private Policy</a>`) }}
						/>
					</div>

					{status !== 200 && !!message && <div className="screen-signup__error">{message}</div>}

					<Button
						type="submit"
						variable="info"
						loading={loading}
						label="Create account"
						className="centered-button"
					/>
				</form>
			</div>
		</ErrorBoundary>
	);
};

export default SignUp;
